import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, CircularProgress, useTheme, useMediaQuery } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AuditContextBar, { accountAvailability } from "../Components/AuditContextBar";
import AuditAccountCard from "../Components/AuditAccountCard";
import AuditAccountSwitcher from "../Components/AuditAccountSwitcher";
import AuditOverviewHeader from "../Components/AuditOverviewHeader";
import ControlsCategoriesView from "../Components/ControlsCategoriesView";
import ControlsHeader from "../Components/ControlsHeader";
import ControlResourcesDrawer from "../Components/ControlResourcesDrawer";
import ErrorUI from "../Components/ErrorUI";
import { AuditRefreshProvider } from "../Components/AuditRefreshContext";
import { ACCOUNT_DETAIL_FONT_SX, AH_WHITE, ComplianceFontProvider, auditHubPageSx } from "../AuditHub/theme";
import { buildComplianceControlsPath, scorePercent } from "../AuditHub/utils";
import { getAudit, unwrapGet } from "../../../helpers/auditHub_helper";
import {
  getComplianceControls,
  getComplianceControlSections,
  clearAllComplianceMessages,
  clearComplianceControls,
} from "../../../store/compliance/complianceSlice";

const ControlsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [selectedSection, setSelectedSection] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedControlId, setSelectedControlId] = useState(null);
  const [selectedResourcesUri, setSelectedResourcesUri] = useState(null);
  const [selectedAvailableTypes, setSelectedAvailableTypes] = useState([]);
  const [selectedAvailableRegions, setSelectedAvailableRegions] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);

  const [auditState, setAuditState] = useState({ status: "idle", audit: null, error: null });
  const auditRequest = useRef(0);

  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const compliance = searchParams.get("compliance");
  const runId = searchParams.get("runId");
  const auditId = searchParams.get("auditId");
  const accountNumber = searchParams.get("accountNumber") || localStorage.getItem("selectedAccountNumber");

  // A silent load keeps the current numbers on screen and only replaces them with a successful response.
  const loadAudit = useCallback(
    (silent) => {
      if (!auditId) return;
      auditRequest.current += 1;
      const requestId = auditRequest.current;
      if (!silent) setAuditState({ status: "loading", audit: null, error: null });
      getAudit(auditId)
        .then((res) => {
          if (auditRequest.current === requestId) setAuditState({ status: "ready", audit: unwrapGet(res), error: null });
        })
        .catch((error) => {
          if (auditRequest.current !== requestId || silent) return;
          setAuditState({ status: "error", audit: null, error: error?.message || "Unable to load this assignment." });
        });
    },
    [auditId]
  );
  const refreshAudit = useCallback(() => loadAudit(true), [loadAudit]);

  useEffect(() => {
    if (!auditId) {
      auditRequest.current += 1;
      setAuditState({ status: "idle", audit: null, error: null });
      return undefined;
    }
    loadAudit(false);
    // Scans finish and other reviewers decide controls elsewhere, so the audit is re-read when the user returns.
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") loadAudit(true);
    };
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      auditRequest.current += 1;
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [auditId, loadAudit]);

  const auditAccounts = Array.isArray(auditState.audit?.accounts) ? auditState.audit.accounts : [];
  const currentAuditAccount = auditAccounts.find((account) => account.accountNumber === accountNumber) || null;
  const auditScope = currentAuditAccount ? accountAvailability(currentAuditAccount, compliance).scope : null;
  const latestRunId = auditScope?.latestRunId || null;
  const latestRunEndedAt = auditScope?.latestRunEndedAt || null;

  // When a newer scan of this account finishes, move to it so every number on the page is from that run.
  useEffect(() => {
    if (!auditId || !compliance || !latestRunId || latestRunId === runId) return;
    if (latestRunEndedAt) localStorage.setItem("selectedScanDate", latestRunEndedAt);
    history.replace(
      buildComplianceControlsPath({ frameworkName: compliance, runId: latestRunId, accountNumber, auditId })
    );
  }, [auditId, compliance, latestRunId, latestRunEndedAt, runId, accountNumber, history]);

  const auditControlCounts = useMemo(() => {
    if (!auditScope?.hasScanResult || !auditScope.components) return null;
    const count = (key) => Number(auditScope.components[key]) || 0;
    return {
      passed: count("passedControls") + count("manualPassed"),
      failed: count("failedControls") + count("manualFailed"),
    };
  }, [auditScope]);

  const handleAccountChange = (nextAccountNumber) => {
    if (!nextAccountNumber || nextAccountNumber === accountNumber) return;
    const account = auditAccounts.find((item) => item.accountNumber === nextAccountNumber);
    const { scope, reason } = account ? accountAvailability(account, compliance) : { scope: null, reason: "missing" };
    if (reason || !scope?.latestRunId) return;
    localStorage.setItem("selectedAccountNumber", nextAccountNumber);
    if (scope.latestRunEndedAt) localStorage.setItem("selectedScanDate", scope.latestRunEndedAt);
    history.replace(
      buildComplianceControlsPath({ frameworkName: compliance, runId: scope.latestRunId, accountNumber: nextAccountNumber, auditId })
    );
  };

  // Redux selectors
  const {
    complianceControlSections,
    isGettingComplianceControlSections,
    getComplianceControlSectionsError,
    complianceControls,
    isGettingComplianceControls,
    getComplianceControlsError,
  } = useSelector((state) => state.compliance);

  const accountSwitcher =
    auditId && compliance && auditAccounts.length > 1 ? (
      <AuditAccountSwitcher
        accounts={auditAccounts}
        compliance={compliance}
        accountNumber={accountNumber}
        onChange={handleAccountChange}
      />
    ) : null;
  const sectionsReady =
    !isGettingComplianceControlSections &&
    !getComplianceControlSectionsError &&
    Array.isArray(complianceControlSections?.controlsListJson) &&
    complianceControlSections.controlsListJson.length > 0;

  // Inside an audit the account's own scope is authoritative; local storage only holds the last dashboard click.
  const scanDate = auditScope?.latestRunEndedAt || localStorage.getItem("selectedScanDate");
  const compliancePercent =
    (auditScope?.hasScanResult ? scorePercent(auditScope.auditScore) : null) ??
    localStorage.getItem("selectedCompliancePercent");

  // Fetch sections on mount or param change
  useEffect(() => {
    dispatch(clearComplianceControls());
    dispatch(clearAllComplianceMessages());
    setSelectedSection(null);
    if (compliance && runId) {
      dispatch(getComplianceControlSections({ compliance, runId }));
    }
  }, [compliance, runId, dispatch]);

  // Fetch controls for selected section
  useEffect(() => {
    if (selectedSection) {
      dispatch(getComplianceControls({ compliance, runId, section: selectedSection }));
    }
  }, [selectedSection, compliance, runId, dispatch]);

  // Handler for section selection
  const handleSectionSelect = (section) => {
    console.log("🚀 ~ handleSectionSelect ~ section:", section)
    setSelectedSection(section);
  };

  // Handler for resource drawer
  const handleResourceClick = (controlId, resourcesUri, row) => {
    setSelectedControlId(controlId);
    setSelectedResourcesUri(resourcesUri);
    setSelectedAvailableTypes(row.AvailableTypes || []);
    setSelectedAvailableRegions(row.AvailableRegions || []);
    setSelectedRunId(runId);
    setDrawerOpen(true);
  };

  return (
    <ComplianceFontProvider>
    <AuditRefreshProvider value={refreshAudit}>
    <Box sx={{ 
      padding: isMobile ? "1rem" : isTablet ? "1.5rem" : "1.5rem 2rem", 
      mb: "3rem", 
      backgroundColor: auditId ? AH_WHITE : "#F9F9F9", 
      minHeight: "100vh", 
      display: "flex", 
      flexDirection: "column", 
      gap: 2,
      overflow: 'hidden', // Prevent horizontal scroll
      maxWidth: '100vw', // Ensure it doesn't exceed viewport width
      ...(auditId ? auditHubPageSx : ACCOUNT_DETAIL_FONT_SX),
    }}>
      {auditId ? (
        <AuditContextBar auditId={auditId} state={auditState} />
      ) : null}
      {auditId && compliance && auditAccounts.length > 1 ? (
        <AuditOverviewHeader
          audit={auditState.audit}
          compliance={compliance}
          accountNumber={accountNumber}
          onAccountChange={handleAccountChange}
        />
      ) : null}
      {/* The account card is not drawn without results, so the switcher stays reachable here. */}
      {accountSwitcher && runId && !isGettingComplianceControlSections && !sectionsReady ? (
        <Box sx={{ display: "flex", maxWidth: { xs: "100%", sm: 380 } }}>{accountSwitcher}</Box>
      ) : null}
      {!compliance || !runId ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh" width="100%">
          <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', px: 2 }}>
            Missing required parameters. Please select a compliance framework from the dashboard.
          </Typography>
        </Box>
      ) : isGettingComplianceControlSections ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh" width="100%">
          <CircularProgress color="primary" />
        </Box>
      ) : getComplianceControlSectionsError ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh" width="100%">
          <Box width="100%">
            <ErrorUI
              error={getComplianceControlSectionsError}
              onRetry={() => dispatch(getComplianceControlSections({ compliance, runId }))}
            />
          </Box>
        </Box>
      ) : !complianceControlSections ||
        !Array.isArray(complianceControlSections.controlsListJson) ||
        complianceControlSections.controlsListJson.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh" width="100%">
          <Typography variant="h6" color="text.secondary">
            No controls data available
          </Typography>
        </Box>
      ) : (
        <>
          {auditId ? (
            <AuditAccountCard
              sections={complianceControlSections}
              compliance={compliance}
              scanDate={scanDate}
              compliancePercent={compliancePercent}
              accountNumber={accountNumber}
              controlCounts={auditControlCounts}
              accountSwitcher={accountSwitcher}
            />
          ) : (
            <ControlsHeader
              controls={{
                controlsListJson: complianceControlSections.controlsListJson,
                trendScore: complianceControlSections.trendScore,
                totalResources: complianceControlSections.totalResources,
                passedResources: complianceControlSections.passedResources,
                failedResources: complianceControlSections.failedResources,
              }}
              compliance={compliance}
              scanDate={scanDate}
              compliancePercent={compliancePercent}
              runId={runId}
              accountNumber={accountNumber}
            />
          )}
          <Box sx={{ 
            flex: 1, 
            display: "flex", 
            flexDirection: "column", 
            minHeight: 0,
            overflow: 'hidden', // Prevent overflow
          }}>
            <ControlsCategoriesView
              controls={complianceControlSections.controlsListJson}
              onSectionSelect={handleSectionSelect}
              selectedSection={selectedSection}
              sectionControls={complianceControls}
              isLoadingControls={isGettingComplianceControls}
              controlsError={getComplianceControlsError}
              onResourceClick={handleResourceClick}
              compliance={compliance}
              runId={runId}
              accountNumber={accountNumber}
              variant={auditId ? "audit" : "default"}
            />
          </Box>
          <ControlResourcesDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            controlId={selectedControlId}
            resourcesUri={selectedResourcesUri}
            availableTypes={selectedAvailableTypes}
            availableRegions={selectedAvailableRegions}
            runId={selectedRunId}
          />
        </>
      )}
    </Box>
    </AuditRefreshProvider>
    </ComplianceFontProvider>
  );
};

export default ControlsPage;
